;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import { ApolloLink, HttpLink } from "@apollo/client"
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors"
import { ErrorLink } from "@apollo/client/link/error"
import { SetContextLink } from "@apollo/client/link/context"
import { RetryLink } from "@apollo/client/link/retry"
import { getMainDefinition } from "@apollo/client/utilities"
import { Observable, type Subscription } from "rxjs"
import {
  AuthenticationRequiredError,
  type ClientSessionController,
} from "../auth/clientSession"

export function isAuthenticationError(error: unknown) {
  return (
    error instanceof AuthenticationRequiredError ||
    (ServerError.is(error) && error.statusCode === 401) ||
    (CombinedGraphQLErrors.is(error) &&
      error.errors.some(
        ({ message, extensions }) =>
          extensions?.code === "UNAUTHENTICATED" || /jwt expired/i.test(message),
      ))
  )
}

export function isTransientQueryError(error: unknown) {
  if (ServerError.is(error))
    return [408, 429, 500, 502, 503, 504].includes(error.statusCode)
  return (
    error instanceof Error &&
    (error.name === "TimeoutError" ||
      (error.name === "TypeError" &&
        /failed to fetch|load failed|networkerror|network request failed/i.test(
          error.message,
        )))
  )
}

// Downstream of retries: every attempt gets a new deadline and retains the
// caller's cancellation signal. Clear timers/listeners when the body completes.
export function requestTimeoutLink(timeoutMs: number) {
  return new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        const controller = new AbortController()
        const options = operation.getContext().fetchOptions ?? {}
        const callerSignal = options.signal as AbortSignal | undefined
        const abort = () => controller.abort(callerSignal?.reason)
        if (callerSignal?.aborted) abort()
        else callerSignal?.addEventListener("abort", abort, { once: true })
        const timer = setTimeout(
          () =>
            controller.abort(new DOMException("Request timed out", "TimeoutError")),
          timeoutMs,
        )
        operation.setContext({
          fetchOptions: { ...options, signal: controller.signal },
        })
        let sub: Subscription | undefined
        if (controller.signal.aborted) observer.error(controller.signal.reason)
        else sub = forward(operation).subscribe(observer)
        return () => {
          clearTimeout(timer)
          callerSignal?.removeEventListener("abort", abort)
          // A retry must not inherit the previous attempt's deadline.
          operation.setContext({ fetchOptions: options })
          sub?.unsubscribe()
          controller.abort()
        }
      }),
  )
}

export function createSessionLink(
  session: ClientSessionController,
  reportError: (
    error: unknown,
    tags: Record<string, string | number | boolean>,
  ) => void,
  onRefresh?: () => void,
) {
  const reportingLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        const report = (error: unknown) => {
          if (error instanceof Error && error.name === "AbortError") return
          if (isAuthenticationError(error)) {
            if (operation.getContext().public !== true) session.expire()
            return
          }
          reportError(error, {
            operation: operation.operationName,
            errorType: CombinedGraphQLErrors.is(error)
              ? "graphql_error"
              : "network_error",
            authState: session.getSession()?.user?.idToken
              ? "authenticated"
              : "anonymous",
            ...(ServerError.is(error) ? { statusCode: error.statusCode } : {}),
          })
        }
        return forward(operation).subscribe({
          next(result) {
            if (result.errors?.length) report(new CombinedGraphQLErrors(result))
            observer.next(result)
          },
          error(error) {
            report(error)
            observer.error(error)
          },
          complete() {
            observer.complete()
          },
        })
      }),
  )

  const sessionCancellationLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        const unsubscribe = session.subscribe(() => {
          observer.error(new DOMException("Session changed", "AbortError"))
        })
        const sub = forward(operation).subscribe(observer)
        return () => {
          unsubscribe()
          sub.unsubscribe()
        }
      }),
  )

  const recoveryLink = new ErrorLink(({ error, operation, forward }) => {
    if (!isAuthenticationError(error) || operation.getContext().public === true)
      return
    if (!session.getSession()?.user?.idToken) return
    return new Observable((observer) => {
      let sub: Subscription | undefined
      session
        .refresh()
        .then(() => {
          if (!observer.closed) {
            onRefresh?.()
            sub = forward(operation).subscribe(observer)
          }
        })
        .catch((error) => observer.error(error))
      return () => sub?.unsubscribe()
    })
  })

  return ApolloLink.from([reportingLink, sessionCancellationLink, recoveryLink])
}

export function createAuthenticatedLink({
  uri,
  session,
  fetch: fetchImplementation,
  reportError,
  timeoutMs = 30_000,
  retryDelay = 300,
}: {
  uri: string
  session: ClientSessionController
  fetch?: typeof fetch
  reportError: (
    error: unknown,
    tags: Record<string, string | number | boolean>,
  ) => void
  timeoutMs?: number
  retryDelay?: number
}) {
  const retryLink = new RetryLink({
    delay: { initial: retryDelay, max: 3000, jitter: true },
    attempts: {
      max: 3,
      retryIf: (error, operation) => {
        const definition = getMainDefinition(operation.query)
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "query" &&
          isTransientQueryError(error)
        )
      },
    },
  })

  const authLink = new SetContextLink(async (context) => {
    const token = context.public === true ? null : await session.getToken()
    const headers = new Headers(context.headers)
    headers.delete("authorization")
    if (token) headers.set("authorization", `Bearer ${token}`)
    return { headers: Object.fromEntries(headers.entries()) }
  })

  return ApolloLink.from([
    createSessionLink(session, reportError),
    retryLink,
    authLink,
    requestTimeoutLink(timeoutMs),
    new HttpLink({ uri, credentials: "same-origin", fetch: fetchImplementation }),
  ])
}
